import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { ApiException } from '@/api/types'
import { captureException } from '@/lib/sentry'

// 401/403/404 are expected, app-handled outcomes — reporting them to Sentry is noise.
function reportUnexpected(error: unknown, context: Record<string, unknown>) {
  if (
    ApiException.isUnauthorized(error) ||
    ApiException.isForbidden(error) ||
    ApiException.isNotFound(error)
  ) {
    return
  }
  captureException(error, context)
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Dev-tools admin probes (review-apps / brand-users) hit external staging
      // hosts with a build-time key and fail with expected 401/CORS errors —
      // these are raw Errors (not ApiException), so reportUnexpected can't filter
      // them. They're internal-tooling noise, not product bugs, so skip them.
      if (query.queryKey[0] === 'admin') return
      reportUnexpected(error, { source: 'query', queryKey: query.queryKey })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportUnexpected(error, { source: 'mutation', mutationKey: mutation.options.mutationKey })
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        if (ApiException.isUnauthorized(error)) return false
        if (ApiException.isForbidden(error)) return false
        if (ApiException.isNotFound(error)) return false
        return failureCount < 3
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
})

export const queryKeys = {
  all: ['all'] as const,

  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.users.lists(), params] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },

  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    // The KPI engine returns tiles + timeline + top-products + example-creators in
    // one call, so a single key covers the whole dashboard payload.
    overview: (params: object) => [...queryKeys.dashboard.all, 'overview', params] as const,
    // The Content archive is its own endpoint (GET /brand/contents), not part of
    // the KPI engine payload.
    content: (params: object) => [...queryKeys.dashboard.all, 'content', params] as const,
  },

  performance: {
    all: ['performance'] as const,
    overview: (params: object) => [...queryKeys.performance.all, 'overview', params] as const,
  },

  influencer: {
    all: ['influencer'] as const,
    overview: (params: object) => [...queryKeys.influencer.all, 'overview', params] as const,
  },

  settings: {
    all: ['settings'] as const,
    team: () => [...queryKeys.settings.all, 'team'] as const,
    shops: () => [...queryKeys.settings.all, 'shops'] as const,
  },

  placements: {
    all: ['placements'] as const,
    contacts: () => [...queryKeys.placements.all, 'contacts'] as const,
  },
}
